// `useExtensionPresence()` — best-effort probe for the Barel MV3 extension.
//
// Behaviour:
//   - Returns `'unknown'` until the probe resolves so the UI doesn't flash
//     a "not detected" state in the no-op browser case (Firefox, Safari).
//   - Posts a `PING` message to the extension via `chrome.runtime.sendMessage`
//     when the extension id is known. If `chrome.runtime` is undefined we
//     short-circuit to `'absent'` after one tick.
//
// The actual extension contract (Wave 3) ships `usePairExtension()` and
// `useExtensionStatus()` server-side; this hook is purely a UX aid for the
// step-3 banner ("Extension détectée — clique pour connecter").

import { useEffect, useState } from 'react';

export type ExtensionPresence = 'unknown' | 'present' | 'absent';

interface ChromeRuntime {
  sendMessage?: (
    extensionId: string,
    message: unknown,
    callback?: (response: unknown) => void,
  ) => void;
  lastError?: { message?: string };
}

interface ChromeApi {
  runtime?: ChromeRuntime;
}

declare global {
  interface Window {
    chrome?: ChromeApi;
  }
}

export function useExtensionPresence(extensionId?: string): ExtensionPresence {
  const [presence, setPresence] = useState<ExtensionPresence>('unknown');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const chrome = window.chrome;
    if (!chrome?.runtime?.sendMessage || !extensionId) {
      // Non-Chrome browser or unconfigured probe — bail out fast.
      setPresence('absent');
      return;
    }
    let settled = false;
    const finish = (next: ExtensionPresence) => {
      if (settled) return;
      settled = true;
      setPresence(next);
    };
    try {
      chrome.runtime.sendMessage(extensionId, { type: 'barel:ping' }, () => {
        if (chrome.runtime?.lastError) {
          finish('absent');
          return;
        }
        finish('present');
      });
    } catch {
      finish('absent');
    }
    // Hard timeout — if the extension never responds we fall back to absent.
    const timeout = window.setTimeout(() => {
      finish('absent');
    }, 1500);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [extensionId]);

  return presence;
}
